import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import './firebase';
import VueGtag, { useGtag } from 'vue-gtag-next';

import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useUserStore } from '@/store/userStore';
import { useDemoStore } from '@/store/demoStore';
import { initHapticInteractions } from '@/utils/haptics';

const app = createApp(App);
const pinia = createPinia();

app
  .use(pinia)
  .use(router)
  // Initialize Google Analytics plugin
  .use(VueGtag, {
    property: { id: process.env.VUE_APP_GA_ID || '' },
    isEnabled: !!process.env.VUE_APP_GA_ID
  });

initHapticInteractions();

// Manual page_view tracking for GA and toggle static landing visibility
const gtag = useGtag();

function setLandingVisible(visible: boolean) {
  try {
    const el = document.getElementById('landing-root');
    if (el) el.style.display = visible ? '' : 'none';
  } catch {
    /* no-op */
  }
}

router.afterEach((to) => {
  // Show static landing only on exact root path
  setLandingVisible(to.path === '/');
  gtag.pageview({
    page_path: to.fullPath,
    page_title: (to.meta.title as string) || to.fullPath
  });
  // Dispatch render-event for Prerender SPA Plugin when on root
  if (to.path === '/') {
    document.dispatchEvent(new Event('render-event'));
  }
});

// Force a full page reload when leaving the P1 Creating Templates view to any non-root route.
// Rationale: ensures a clean environment (e.g., clears in‑memory generation state) when users
// move away from the creation flow to other sections of the app.
const P1_CREATE_ROUTE_NAMES = new Set([
  'p1createTemplates',
  'p1createTemplatesNew'
]);
router.afterEach((to, from) => {
  // We only care about navigations where the previous route was one of the P1 create templates views
  // and the destination isn't the static landing page (root path '/').
  if (
    from &&
    P1_CREATE_ROUTE_NAMES.has(from.name as string) &&
    to.path !== '/'
  ) {
    // Defer so the navigation state updates before reload; avoids aborting navigation mid-flight.
    setTimeout(() => {
      // Double‑check we didn't somehow navigate back into the same create route before firing.
      if (!P1_CREATE_ROUTE_NAMES.has(to.name as string)) {
        window.location.reload();
      }
    }, 0);
  }
});

const auth = getAuth();

// Track if we've already mounted the app
let appIsMounted = false;

onAuthStateChanged(auth, async (firebaseUser) => {
  const userStore = useUserStore();
  const demoStore = useDemoStore();

  if (firebaseUser) {
    const token = await firebaseUser.getIdToken();
    userStore.idToken = token;
    console.log('User is logged in, token set:', token);

    // If a real user is logged in, clear any stale demo state.
    // The demo state persists in localStorage and can leak into real sessions.
    if (demoStore.isDemo) {
      // Check if the current route is NOT the demo project — if so, clear demo
      const currentPath = window.location.pathname;
      const demoId = demoStore.demoTenantId;
      if (!demoId || !currentPath.includes(demoId)) {
        console.warn(
          '[main] Real user signed in on non-demo route — clearing stale demo state'
        );
        demoStore.clearDemo();
        // Re-set the real user's token since clearDemo clears userStore
        userStore.idToken = token;
      }
    }
  } else if (!demoStore.isDemo) {
    // Only clear token if NOT in demo mode — demo mode sets its own token
    userStore.idToken = null;
    console.log('No user is logged in');
  }

  // Only mount once
  if (!appIsMounted) {
    app.mount('#app');
    appIsMounted = true;
  }
});
